<script setup>
// Escolha de modelo por TIER, não por modelo.
//
// Antes a pessoa escolhia principal e fallback em dois seletores, com cinco modelos soltos e
// preço na tela. Isso pede uma decisão que quase ninguém tem como tomar bem — e a decisão que
// importa de verdade é outra: "quero o mais capaz ou o mais barato?". O fallback é consequência
// dessa escolha, não uma segunda pergunta.
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Input from 'dashboard/components-next/input/Input.vue';
import { TIERS, rotuloCurto } from './modelCatalog';

const props = defineProps({
  primary: { type: String, default: '' },
  fallback: { type: String, default: '' },
  catalog: { type: Array, default: () => [] },
});
const emit = defineEmits(['pick']);
const { t } = useI18n();

// A faixa é o PAR: o mesmo principal com outra reserva (ou sem reserva) não é a faixa. Pelo
// principal só, um agente Luna sem reserva aparecia marcado como Faixa 1, com o cartão dizendo
// "Reserva: Haiku" e o resumo dizendo "sem reserva".
const tierAtual = computed(() => {
  const achado = TIERS.find(
    x => x.primary === props.primary && x.fallback === props.fallback
  );
  return achado ? achado.tier : null;
});

const meta = id => props.catalog.find(m => m.id === id);
const nome = id => rotuloCurto(meta(id)?.label) || id;

// Fora das faixas, o super admin escolhe qualquer modelo do OpenRouter pelo id (decisão do
// Murilo, 23/09/2026); o servidor confere se o id existe e aceita ferramentas.
//
// A opção nasce marcada quando o agente JÁ está num modelo fora das faixas. A tela não pode
// ficar calada sobre isso: sem nada marcado, os dois cartões descrevem modelos do catálogo e
// leem como se um deles fosse o do agente -- foi assim que um agente rodando
// `google/gemini-2.5-flash` apareceu como "GPT-5.6 Luna", e só o rastro contava a verdade.
const personalizadoAberto = ref(false);
const personalizado = computed(
  () =>
    personalizadoAberto.value ||
    (Boolean(props.primary) && tierAtual.value === null)
);
const idPrincipal = ref(tierAtual.value === null ? props.primary : '');
const idReserva = ref(tierAtual.value === null ? props.fallback : '');

function escolherFaixa(opcao) {
  personalizadoAberto.value = false;
  emit('pick', opcao);
}

// Abre com o que o agente usa hoje, para quem vai trocar ver de onde parte.
function abrirPersonalizado() {
  if (personalizado.value) return;
  idPrincipal.value = props.primary;
  idReserva.value = props.fallback;
  personalizadoAberto.value = true;
}

// As duas faixas e o personalizado são UMA escolha (um radiogroup): antes o personalizado era
// um bloco à parte, e a tela tinha dois "marcados" possíveis com gramáticas diferentes.
const opcoes = computed(() => [
  ...TIERS.map(faixa => ({ chave: `tier-${faixa.tier}`, faixa })),
  { chave: 'custom', faixa: null },
]);
const marcada = opcao =>
  opcao.faixa
    ? !personalizado.value && tierAtual.value === opcao.faixa.tier
    : personalizado.value;
const indiceMarcado = computed(() => opcoes.value.findIndex(marcada));

function escolher(opcao) {
  if (opcao.faixa) escolherFaixa(opcao.faixa);
  else abrirPersonalizado();
}

// Setas andam entre os cartões e escolhem, como num radio nativo; o foco vai junto.
const cartoes = ref([]);
async function teclado(evento) {
  const passo = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[
    evento.key
  ];
  if (!passo) return;
  evento.preventDefault();
  const total = opcoes.value.length;
  const de = indiceMarcado.value === -1 ? 0 : indiceMarcado.value;
  const para = (de + passo + total) % total;
  escolher(opcoes.value[para]);
  await nextTick();
  cartoes.value[para]?.focus();
}

// O que se digita já vale como escolha; quem grava é o Salvar da seção. Com o principal em
// branco não sai nada: gravar `model_primary` vazio faria o runtime cair calado no padrão.
watch([idPrincipal, idReserva], ([principal, reserva]) => {
  if (!personalizado.value || !principal.trim()) return;
  emit('pick', {
    tier: null,
    primary: principal.trim(),
    fallback: reserva.trim() || props.fallback,
  });
});
</script>

<template>
  <div class="flex flex-col gap-3">
    <div
      role="radiogroup"
      :aria-label="t('POWER_AGENTS.MODELS.TITLE')"
      class="grid gap-3 sm:grid-cols-3"
      @keydown="teclado"
    >
      <button
        v-for="(opcao, i) in opcoes"
        :key="opcao.chave"
        ref="cartoes"
        type="button"
        role="radio"
        :aria-checked="marcada(opcao)"
        :tabindex="
          i === indiceMarcado || (indiceMarcado === -1 && i === 0) ? 0 : -1
        "
        :data-opcao="opcao.chave"
        class="flex flex-col gap-3 p-4 text-left border border-solid rounded-xl transition-colors duration-150 ease-out outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand"
        :class="
          marcada(opcao)
            ? 'border-n-brand bg-n-alpha-1'
            : 'border-n-weak bg-n-solid-1 hover:border-n-slate-7'
        "
        @click="escolher(opcao)"
      >
        <span class="flex items-start justify-between w-full gap-3">
          <span class="min-w-0">
            <span class="block text-sm font-semibold text-n-slate-12">
              {{
                opcao.faixa
                  ? t(`POWER_AGENTS.MODELS.TIER_${opcao.faixa.tier}`)
                  : t('POWER_AGENTS.MODELS.CUSTOM')
              }}
            </span>
            <span class="block mt-1 text-xs text-n-slate-11">
              {{
                opcao.faixa
                  ? t(`POWER_AGENTS.MODELS.TIER_${opcao.faixa.tier}_HINT`)
                  : t('POWER_AGENTS.MODELS.CUSTOM_HINT')
              }}
            </span>
          </span>
          <!-- O indicador de rádio diz "escolhido" sem depender só da cor da borda. -->
          <span
            class="mt-0.5 rounded-full border-solid size-4 shrink-0 transition-[border-width,border-color] duration-150 ease-out"
            :class="
              marcada(opcao)
                ? 'border-[5px] border-n-brand'
                : 'border border-n-slate-8'
            "
          />
        </span>

        <!-- O par é mostrado, não escolhido: quem escolhe a faixa já escolheu a reserva. -->
        <span
          v-if="opcao.faixa"
          class="flex flex-col w-full gap-1 pt-3 mt-auto text-xs border-0 border-t border-solid border-n-weak"
        >
          <span class="flex items-baseline justify-between gap-2">
            <span class="text-n-slate-11">
              {{ t('POWER_AGENTS.MODELS.PRIMARY') }}
            </span>
            <span class="font-medium truncate text-n-slate-12">
              {{ nome(opcao.faixa.primary) }}
            </span>
          </span>
          <span class="flex items-baseline justify-between gap-2">
            <span class="text-n-slate-11">
              {{ t('POWER_AGENTS.MODELS.FALLBACK') }}
            </span>
            <span class="truncate text-n-slate-11">
              {{ nome(opcao.faixa.fallback) }}
            </span>
          </span>
          <span
            v-if="meta(opcao.faixa.primary)?.cache_economy"
            class="flex items-center gap-1 pt-1 text-n-slate-11"
          >
            <i class="i-lucide-zap size-3 shrink-0 text-n-teal-10" />
            {{
              t('POWER_AGENTS.MODELS.CACHE_SHORT', {
                n: meta(opcao.faixa.primary).cache_economy,
              })
            }}
          </span>
        </span>
      </button>
    </div>

    <div
      v-if="personalizado"
      class="flex flex-col gap-3 p-4 rounded-xl bg-n-alpha-1"
    >
      <div class="grid gap-3 sm:grid-cols-2">
        <Input
          v-model="idPrincipal"
          :label="t('POWER_AGENTS.MODELS.CUSTOM_PRIMARY')"
          :placeholder="t('POWER_AGENTS.MODELS.CUSTOM_PLACEHOLDER')"
          custom-input-class="font-mono"
        />
        <Input
          v-model="idReserva"
          :label="t('POWER_AGENTS.MODELS.CUSTOM_FALLBACK')"
          :placeholder="t('POWER_AGENTS.MODELS.CUSTOM_PLACEHOLDER')"
          custom-input-class="font-mono"
        />
      </div>
      <a
        href="https://openrouter.ai/models"
        target="_blank"
        rel="noopener noreferrer"
        class="inline-flex items-center gap-1 text-xs underline underline-offset-2 text-n-brand w-fit"
      >
        {{ t('POWER_AGENTS.MODELS.CUSTOM_LINK') }}
        <i class="i-lucide-arrow-up-right size-3" />
      </a>
    </div>
  </div>
</template>
